import { useState, useEffect } from 'react';

import type { PageCardAppearance } from '@confluence/page-card/entry-points/cardTypes';
import { useSessionData } from '@confluence/session-data';

import {
	useLoggedInHomeViewsUserPreferences,
	useAnonymousHomeViewsUserPreferences,
} from './useHomeViewsUserPreferences';
import { PagesDisplayPersistenceOption } from './__types__/HomeViewsUserPreferencesQuery';
import type { OptionsProps } from './Options';
import type { PillFilter } from './Pills';

type UseOptions = (pills?: PillFilter[], defaultPill?: string) => OptionsProps;

const AppearancePersistenceToToggle: {
	[key in PagesDisplayPersistenceOption]: PageCardAppearance;
} = {
	[PagesDisplayPersistenceOption.CARDS]: 'grid',
	[PagesDisplayPersistenceOption.LIST]: 'list',
	[PagesDisplayPersistenceOption.COMPACT_LIST]: 'compact-list',
};

const AppearanceToggleToPersistence: {
	[key in PageCardAppearance]: PagesDisplayPersistenceOption;
} = {
	grid: PagesDisplayPersistenceOption.CARDS,
	list: PagesDisplayPersistenceOption.LIST,
	'compact-list': PagesDisplayPersistenceOption.COMPACT_LIST,
	'popup-view': PagesDisplayPersistenceOption.CARDS,
	'end-of-page': PagesDisplayPersistenceOption.CARDS,
	'compact-end-of-page': PagesDisplayPersistenceOption.CARDS,
};

export const useOptions: UseOptions = (pills?, defaultPill?) => {
	const { isLoggedIn } = useSessionData();
	const [filterText, setFilterText] = useState('');
	const [pill, setPill] = useState(defaultPill);

	/**
	 * The default pill will only change if an override from query params changes.
	 * In this case, we need to update the state in order for the UI to reflect
	 * the new pill.
	 */
	useEffect(() => {
		setPill(defaultPill);
	}, [defaultPill]);

	const loggedInUserPreferences = useLoggedInHomeViewsUserPreferences();
	const anonymousUserPreferences = useAnonymousHomeViewsUserPreferences();

	const { loading, queryError, mutationError, appearance, updateHomeViewsUserPreference } =
		isLoggedIn ? loggedInUserPreferences : anonymousUserPreferences;

	const setAppearance = (appearance: PageCardAppearance) => {
		updateHomeViewsUserPreference(AppearanceToggleToPersistence[appearance]);
	};

	const optionsData: OptionsProps = {
		loading,
		queryError,
		mutationError,
		filterText,
		setFilterText,
		appearance: AppearancePersistenceToToggle[appearance],
		setAppearance,
	};

	if (pills) {
		optionsData.pill = pill;
		optionsData.pill = pill;
		optionsData.setPill = setPill;
		optionsData.pills = pills;
	}

	return optionsData;
};
